import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { useBillingBills } from "@/common/hooks/queries/useBillingBills";
import { FileText, SearchIcon } from "lucide-react";
import billsTableColumns from "../../constants/billsTableColumns.constant";
import ListFilters from "@/common/components/ListFilters";
import { billingBillStatusOptions } from "../../constants/billingBillStatus.constant";
import { Mapper } from "@/common/services/mapper";
import { BillsTransformer } from "../../services/billsTransformer";
import { BillData } from "../../types/bills.type";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";

const BillsList = () => {
  const { filterParams, setFilterParams } = useFilterParams({
    status: billingBillStatusOptions.find(({ value }) => value === 'pending_payment'),
  });

  const { date, formattedDate } = useFilterDate(filterParams);

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const {
    data: bills,
    meta,
    isPending: isPendingBills,
    error: billsError = {} as any,
    refetch: refetchBills,
  } = useBillingBills({
    filters: {
      search,
      page: filterParams?.page || 1,
      status: filterParams?.status?.value,
      startDate: formattedDate.from,
      endDate: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer<BillData[], any[]>({
      transformer: BillsTransformer.tableTransformer(),
    }),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <ListHeader
          Icon={<FileText className="w-10 h-10" strokeWidth={1.5} />}
          title="Faturas"
        />
      </div>

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={billsError?.status}
        refetchFunction={refetchBills}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <div className="flex items-center gap-4">
              <DatePickerWithRange
                date={date}
                setDate={(date) => setFilterParams((params) => ({ ...params, date }))}
              />

              <ListFilters
                filters={[
                  {
                    className: 'min-w-40',
                    onChange: (e: any) => setFilterParams((params) => ({ ...params, status: e })),
                    value: filterParams?.status,
                    options: [{ label: "Todas", value: '' }, ...(billingBillStatusOptions || [])],
                    placeholder: "Status",
                    dataTestid: 'status'
                  },
                ]}
              />
            </div>
          </div>

          <Table
            columns={billsTableColumns}
            isLoadingData={isPendingBills}
            rows={bills}
            rowEmptyValue="--"
            page={filterParams.page || 1}
            setPage={(p) => setFilterParams((params) => ({ ...params, page: p }))}
            totalPages={meta.total_pages}
            totalCount={meta.total}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default BillsList;
